import { BufferAttribute, BufferGeometry, Group, Mesh } from 'three'
import type { MachineDefinition, MachineDevice, SpinnerDevice } from '@core/machine'
import type { BuiltDevice, DeviceRuntime } from './deviceMeshes'
import type { Finish, PartPalette } from './partsKit'
import type asset from '../../SourceArt/PierSpinners/mesh.json'

let data: typeof asset | undefined
export async function prepareAuthoredSpinners(): Promise<void> {
  data ??= (await import('../../SourceArt/PierSpinners/mesh.json')).default
}
const near=(a:number,b:number)=>Math.abs(a-b)<=1e-9
function matches(d:MachineDevice|undefined,e:SpinnerDevice):d is SpinnerDevice {
  return d?.kind==='spinner' && ['a','b'].every(k=>{
    const key=k as 'a'|'b';return near(d[key][0],e[key][0])&&near(d[key][1],e[key][1])
  }) && near(d.vaneMass,e.vaneMass)&&near(d.shaftHeight,e.shaftHeight)
    &&near(d.vaneDrop,e.vaneDrop)&&near(d.damping,e.damping)&&d.switch===e.switch
}
/** Replace only render children; preserve the shaft/hinge objects and existing rebind/animator. */
export function withAuthoredSpinners(built:BuiltDevice,device:MachineDevice,definition:MachineDefinition,
  palette:PartPalette,illustrated:boolean,runtime:DeviceRuntime={}):BuiltDevice {
  if(!illustrated||device.kind!=='spinner'||built.group===undefined)return built
  const item=data?.machines[definition.machineId as keyof typeof asset.machines]?.find(x=>x.id===device.id)
  if(item===undefined||!matches(device,item.device as unknown as SpinnerDevice))return built
  const expected=item.device as unknown as SpinnerDevice
  if(!matches(definition.devices.find(d=>d.id===device.id),expected))return built
  const live=runtime.device
  if(live?.kind==='spinner'){
    const spinner=live as typeof live&{ax:number,ay:number,bx:number,by:number}
    if(!near(spinner.ax,device.a[0])||!near(spinner.ay,device.a[1])||!near(spinner.bx,device.b[0])||!near(spinner.by,device.b[1]))return built
  }
  const root=built.group,frame=root.children.find(c=>c.name===device.id+'.shaft')
  if(!(frame instanceof Group))return built
  const hinge=frame.children.find(c=>c.name===device.id+'.vane')
  if(!(hinge instanceof Group))return built
  const supports=root.children.filter(c=>c instanceof Mesh) as Mesh[]
  const shafts=frame.children.filter(c=>c instanceof Mesh) as Mesh[]
  if(supports.length!==2||shafts.length!==1||hinge.children.length!==1||!(hinge.children[0] instanceof Mesh))return built
  const length=Math.hypot(device.b[0]-device.a[0],device.b[1]-device.a[1])
  if(!near(frame.position.x,(device.a[0]+device.b[0])/2)||!near(frame.position.y,(device.a[1]+device.b[1])/2)
    ||!near(frame.position.z,device.shaftHeight)||!near(frame.rotation.z,Math.atan2(device.b[1]-device.a[1],device.b[0]-device.a[0])))return built
  const parameters=(hinge.children[0].geometry as BufferGeometry&{parameters?:{width:number,height:number,depth:number}}).parameters
  if(parameters===undefined||!near(parameters.width,length-.008)||!near(parameters.height,.0009)||!near(parameters.depth,device.vaneDrop))return built
  const groups=new Map<string,Mesh[]>(),geometries:BufferGeometry[]=[]
  try {
    if(item.parts.length!==6)throw new Error('Spinner requires six groups')
    const unique=new Set<string>()
    for(const p of item.parts){
      const key=p.owner+'/'+p.finish,count=p.positions.length/3
      if(!['bracket0','bracket1','shaft','vane'].includes(p.owner)||!['HardwareSteel','HardwareIvory','HardwareRubber'].includes(p.finish)
        ||unique.has(key)||(p.owner!=='vane'&&p.finish!=='HardwareSteel'))throw new Error('Invalid spinner owner/finish')
      unique.add(key)
      if(count===0||!Number.isInteger(count)||p.normals.length!==p.positions.length||p.uvs.length!==count*2
        ||p.triangles.length===0||p.triangles.length%3!==0||![...p.positions,...p.normals,...p.uvs].every(Number.isFinite)
        ||!p.triangles.every(i=>Number.isInteger(i)&&i>=0&&i<count))throw new Error('Invalid spinner mesh')
      if(p.owner==='vane')for(let j=0;j<p.positions.length;j+=3){
        if(Math.abs(p.positions[j]!)>(length-.008)/2+1e-6||Math.abs(p.positions[j+1]!)>.0016
          ||p.positions[j+2]! < -device.vaneDrop-1e-6||p.positions[j+2]! > .0016)throw new Error('Spinner vane outside physical envelope')
      }
      const g=new BufferGeometry();geometries.push(g)
      g.setAttribute('position',new BufferAttribute(new Float32Array(p.positions),3))
      g.setAttribute('normal',new BufferAttribute(new Float32Array(p.normals),3))
      g.setAttribute('uv',new BufferAttribute(new Float32Array(p.uvs),2));g.setIndex(p.triangles);g.computeBoundingBox();g.computeBoundingSphere()
      const finish=(p.finish[0]!.toLowerCase()+p.finish.slice(1)) as Finish
      const material=p.finish==='HardwareIvory'?palette.satinPostPolymer():p.finish==='HardwareSteel'?palette.satinSpinnerSteel():palette.get(finish)
      const mesh=new Mesh(g,material);mesh.name=device.id+'.spinner.authored.'+p.owner+'.'+p.finish
      const list=groups.get(p.owner)??[];list.push(mesh);groups.set(p.owner,list)
    }
    for(const owner of ['bracket0','bracket1','shaft','vane'])if(groups.get(owner)?.length!==(owner==='vane'?3:1))throw new Error('Incomplete spinner assembly')
  }catch(e){geometries.forEach(g=>g.dispose());throw e}
  root.remove(...supports);frame.remove(...shafts);hinge.remove(...hinge.children)
  // Supports are shaft-local geometry at board height; a fixed child carries the same bearing.
  const mounts=new Group();mounts.name=device.id+'.spinner.authored.mounts'
  mounts.position.set(frame.position.x,frame.position.y,0);mounts.rotation.z=frame.rotation.z
  mounts.add(...groups.get('bracket0')!,...groups.get('bracket1')!);root.add(mounts)
  frame.add(...groups.get('shaft')!);hinge.add(...groups.get('vane')!)
  return {...built,geometries:[...built.geometries,...geometries]}
}
